/* eslint-disable no-nested-ternary */
import { useDispatch, useSelector } from 'react-redux';
import { Group, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  IconMoneybag, IconBusinessplan, IconTrendingDown, IconTrophy,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import {
  BorrowerDetailsTable, DataSummaryCard, Page, ProcessHeader, MortgageInfoBoxes,
} from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { roundNumber } from '@/helpers/number';
import { formatNumberWithCommas } from '@/helpers/string';
import ProcessButton from '@/components/ProcessButton';
import CurrencyNisIcon from '@/assets/images/currencyNis.svg?react'; // eslint-disable-line import/no-unresolved
import HandshakeIcon from '@/assets/images/handshake.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './mortgageCycleSummary.module.css';
import { getMutationResult } from '@/helpers/query';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import {
  useGetAllSupportTypeMutation,
  useSaveMutation,
} from '@/services/api';
import TableSimple from '@/components/TableSimple';

const MortgageCyclesSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const borrowersStore = useSelector((state) => state.proposal.borrower);
  const proposal = useSelector((state) => state.proposal);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [apiSave] = useSaveMutation();
  const [apiGetAllSupportTypes] = useGetAllSupportTypeMutation();
  const mortgageCycleDetails = useSelector((state) => state.mortgageCycle);
  const suffix = <CurrencyNisIcon style={{ width: '12px' }} />;
  const { id } = proposal;
  useIdInStoreGuard();

  const handleContinue = async () => {
    if (user?.role?.code === '5') {
      try {
        let supportType;
        const { data: result } = await getMutationResult({}, apiGetAllSupportTypes);
        const { success, data } = result;
        if (success) {
          supportType = data.find((option) => option.name === 'selfSupport');

          dispatch(updateExtraDetails({
            routeType: supportType,
          }));

          const objToSave = {
            id,
            routeType: supportType?._id,
          };

          await apiSave(objToSave);
        } else {
          notifications.show({
            title: t('globals.error.title'),
            message: t('globals.error.message'),
          });
        }
      } catch (error) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      }

      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'pay').path, { id }));
    } else {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'routeType').path, { id }));
    }
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'mortgageCycleAction').path, { id }));
  };
  // Calculate totals from mortgage cycle data
  const totalLoanAmount = mortgageCycleDetails?.currentLoans?.reduce((sum, loan) => sum
    + (loan.totalAmount || 0), 0) || 0;

  const details = [
    {
      label: t('pages.mortgageCyclesSummary.label.loanType'),
      value: proposal?.extraDetails?.loanTypeNameHebrew,
    },
    {
      label: t('pages.mortgageCyclesSummary.label.borrowerNumber'),
      value: borrowersStore.length,
    },
    {
      label: t('pages.mortgageCyclesSummary.label.loanAmount'),
      value: formatNumberWithCommas(roundNumber(totalLoanAmount)),
      suffix,
    },
  ];

  const mortgageBoxes = useMemo(() => {
    const totalsBeforeCalculation = mortgageCycleDetails.calculationData?.totalsBeforeCalculation;
    const effectiveTotalsAfter = mortgageCycleDetails.calculationData?.totalsAfterCalculation;
    const currentMonthlyPayment = mortgageCycleDetails?.currentLoans?.reduce(
      (sum, loan) => sum + (Number(loan.monthlyPayment) || 0),
      0,
    ) || 0;
    const { mortgageCycleAction } = mortgageCycleDetails;

    // eslint-disable-next-line max-len
    const beforeMonthly = Number(totalsBeforeCalculation?.newMonthlyPayment ?? currentMonthlyPayment);
    const afterMonthly = Number(effectiveTotalsAfter?.newMonthlyPayment ?? 0);
    const monthlySaving = (beforeMonthly - afterMonthly).toFixed(0);

    const beforeTotal = Number(totalsBeforeCalculation?.finalPayment ?? 0);
    const afterTotal = Number(effectiveTotalsAfter?.finalPayment ?? 0);
    const totalSaving = Math.round(beforeTotal - afterTotal);
    const totalSavingPercentage = beforeTotal > 0
      ? Number((((beforeTotal - afterTotal) / beforeTotal) * 100).toFixed(1))
      : 0;

    const getValueColor = (before, after, isLowerBetter = true) => {
      if (before === after) return 'gray';
      const improved = isLowerBetter ? after < before : after > before;
      return improved ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)';
    };

    return [
      {
        title: t('pages.mortgageCycle.newMonthlyPayment'),
        value: `${Math.round(afterMonthly).toLocaleString()} ₪`,
        info: t('pages.mortgageCycle.newMonthlyPaymentInfo'),
        icon: <IconMoneybag color="#00ABAB" />,
        color: getValueColor(beforeMonthly, afterMonthly, true),
        borderColor: '#00ABAB',
      },
      {
        title: t('pages.mortgageCycle.monthlySaving'),
        value: `${Number(monthlySaving).toLocaleString()} ₪`,
        info: t('pages.mortgageCycle.monthlySavingInfo'),
        icon: <IconTrendingDown color="#00ABAB" />,
        color: monthlySaving > 0 ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)',
        borderColor: '#00ABAB',
      },
      {
        title: t('pages.mortgageCycle.totalSaving'),
        value: `${totalSaving.toLocaleString()} ₪`,
        info: t('pages.mortgageCycle.totalSavingInfo', { percentage: totalSavingPercentage }),
        icon: <IconTrophy color="#00ABAB" />,
        color: totalSaving > 0 ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)',
        show: mortgageCycleAction !== 'increasingTheLoanPeriod' && mortgageCycleAction !== 'reductionOfRepaymentAndExtensionOfTheLoanTermThroughLoanConsolidation',
        borderColor: '#00ABAB',
      },
      {
        title: t('pages.mortgageCycle.returnConversionTitle'),
        value: `${Number(effectiveTotalsAfter?.returnConversion).toFixed(2)} ₪`,
        info: t('pages.mortgageCycle.returnConversionInfo', { amount: totalsBeforeCalculation?.returnConversion?.toFixed(2) }),
        icon: <IconBusinessplan color="#00ABAB" />,
        // eslint-disable-next-line max-len
        color: getValueColor(totalsBeforeCalculation?.returnConversion, effectiveTotalsAfter?.returnConversion, true),
        borderColor: '#00ABAB',
      },
    ];
  }, [mortgageCycleDetails, t]);

  const tableHead = [
    t('pages.mortgageCyclesSummary.table.head.month'),
    t('pages.mortgageCyclesSummary.table.head.remaining'),
    t('pages.mortgageCyclesSummary.table.head.monthlyPaymentWithoutInterest'),
    t('pages.mortgageCyclesSummary.table.head.monthlyPaymentInterest'),
    t('pages.mortgageCyclesSummary.table.head.totalPayment'),
  ];

  // Use mortgageCycle.repaymentTableData to populate the repayment table
  const tableBody = mortgageCycleDetails?.repaymentTableData?.map((item, index) => [
    item.monthIndex || index + 1, // month number
    `${formatNumberWithCommas(roundNumber(item.total?.remainingPrincipal || 0))} ₪`,
    `${formatNumberWithCommas(roundNumber(item.total?.principal || 0))} ₪`,
    `${formatNumberWithCommas(roundNumber(item.total?.interest || 0))} ₪`,
    `${formatNumberWithCommas(roundNumber(item.total?.totalPayment || 0))} ₪`,
  ]) || [];

  const disposalboardtableData = {
    head: tableHead,
    body: tableBody,
  };

  return (
    <ProcessHeader
      headerText={t('pages.mortgageCyclesSummary.title')}
      headerColor="#DBFFE1"
      headerTextColor="#00A81C"
      // subtitle={t('pages.mortgageCyclesSummary.subtitle')}
      showReturnButton
      onReturn={handleReturn}
      icon={HandshakeIcon}
    >
      <Page.Head
        title={t('pages.mortgageCyclesSummary.title')}
        subtitle={t('pages.mortgageCyclesSummary.subtitle')}
      />

      <div>
        <DataSummaryCard details={details} />
      </div>

      <div>
        <h3>{t('pages.summary.title.borrowerDetails')}</h3>
        <BorrowerDetailsTable borrowers={borrowersStore} />
      </div>

      <Group mb="lg">
        <Text size="lg" fw={600} className={classes.title}>
          {t('pages.mortgageCyclesSummary.totalsComparison.title')}
        </Text>
      </Group>
      <div>
        <MortgageInfoBoxes boxes={mortgageBoxes} />
      </div>
      {mortgageCycleDetails?.repaymentTableData && (
        <div className={classes.summaryCollapse}>
          <Page.Section
              title={t('pages.mortgageCyclesSummary.title.disposalBoardFor')}
              withCollapse
              withBackground
              startClose
            >
            <TableSimple data={disposalboardtableData} />
          </Page.Section>
        </div>
      )}
      <Page.Actions>
        <ProcessButton onClick={handleContinue} />
      </Page.Actions>
    </ProcessHeader>
  );
};

export default MortgageCyclesSummary;
